import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import OvVideoThumbnail from '../atoms/OvVideoThumbnail';
import OvVisuallyHidden from '../atoms/OvVisuallyHidden';
import { AdminVideo } from '../../../common/model/dto/video/admin-video';
import { formatVideoDuration } from '../../../common/utils/video-file-utils';
import { getVideoOrientation } from '../../../common/utils/video-notice-utils';
import breakpoints from '../../../design-system/breakpoints';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvVideoSummaryProps {
  video: AdminVideo;
  detailsPath: string;
  isUnseen?: boolean;
}

const OvVideoSummary: FC<OvVideoSummaryProps> = ({
  video,
  detailsPath,
  isUnseen = false,
}) => {
  const { t } = useTranslation();

  return (
    <Wrapper>
      <Poster>
        <OvVideoThumbnail
          src={video.poster_url}
          size="wide"
          isDimmed={isUnseen}
        />
        {video.duration_seconds !== null && (
          <Duration>
            <OvVisuallyHidden>{t('videos.list.duration')} </OvVisuallyHidden>
            {formatVideoDuration(video.duration_seconds)}
          </Duration>
        )}
      </Poster>
      <Text>
        <TitleLink to={detailsPath}>{video.title}</TitleLink>
        <Secondary>
          {t(`videos.orientation.${getVideoOrientation(video)}`)}
        </Secondary>
        {isUnseen && (
          <Secondary>
            <VisibilityOffIcon aria-hidden="true" />
            {t('videos.preview.notSeen')}
          </Secondary>
        )}
      </Text>
    </Wrapper>
  );
};

export default OvVideoSummary;

const Wrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${Variables.spacing.MEDIUM};
  min-width: 0;

  @media (min-width: ${breakpoints.lg}) {
    min-width: 18rem;
  }
`;

const Poster = styled.div`
  position: relative;
  flex: none;
`;

const Duration = styled.span`
  position: absolute;
  right: 0.25rem;
  bottom: 0.25rem;
  padding: 0.0625rem 0.3125rem;
  border-radius: ${Variables.borderRadius.SMALL};
  background-color: rgba(0, 0, 0, 0.8);
  color: ${Colours.WHITE};
  font-size: ${Variables.fontSizes.SMALL};
  font-weight: bold;
  font-variant-numeric: tabular-nums;
  line-height: 1.25;
`;

const Text = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
  padding-top: 0.125rem;
`;

const TitleLink = styled(Link)`
  color: ${Colours.OV_BASE};
  font-weight: bold;
  overflow-wrap: anywhere;
  text-underline-offset: 3px;

  &:hover {
    color: ${Colours.OV_ACCENT_BLUE};
  }
`;

const Secondary = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: ${Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};

  .MuiSvgIcon-root {
    width: 0.875rem;
    height: 0.875rem;
  }
`;
